import React, { FC, useEffect, useMemo } from 'react';
import { Control, useWatch } from 'react-hook-form';

import type { StakeholderType } from '../../../../../types/stakeholder.types';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { useLocale } from '../../../../../hooks/index';
import { TemplateSignatoryTypeEntity } from '../../../../documents/templates/components/table/row/template-row.types';
import {
  GRANTEE,
  templateSignatoryTypeTranslation,
} from '../../../../documents/templates/constants/template-signatory-type.constant';
import { useTemplateById } from '../../../../documents/templates/hooks/use-template-by-id.hook';
import { StakeholderCombobox } from '../../components/index';
import { ChildrenFormProps } from '../../event-form.types';
import { FormSchema } from '../../event-form.validation';

const GRANT_BASE_PATH = 'stepOne.eventDetails.grant';

type SortOrderable = { sortOrder?: null | number | string };

const sortByOrder = (a: SortOrderable, b: SortOrderable): number => {
  const toOrder = (s: SortOrderable) => {
    const n = Number(s.sortOrder);
    return Number.isFinite(n) ? n : 0;
  };
  return toOrder(a) - toOrder(b);
};

type GrantItemForRecipient = {
  stakeholder?: { fullName?: string; id?: string } | null;
};

type ConfirmRecipientCardProps = {
  companyId: string;
  control: Control<FormSchema>;
  excludeStakeholderIds: string[];
  grantItems: GrantItemForRecipient[];
  index: number;
  signatoryType: TemplateSignatoryTypeEntity;
};

const ConfirmRecipientCard: FC<ConfirmRecipientCardProps> = React.memo(
  ({ companyId, control, excludeStakeholderIds, grantItems, index, signatoryType }) => {
    const { messagesLocale } = useLocale();
    const isGrantee = signatoryType.stakeholderType === GRANTEE;
    const typeMessageId = templateSignatoryTypeTranslation[signatoryType.stakeholderType];
    const typeLabel = messagesLocale[typeMessageId] ?? signatoryType.stakeholderType;

    return (
      <div className="flex flex-col gap-2 rounded-lg border border-gray-25 bg-gray-25 p-4 shadow-[0_-1px_3px_0_rgba(16,24,40,0.04),0_2px_3px_0_rgba(16,24,40,0.10),0_1px_2px_0_rgba(16,24,40,0.06)]">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span
              className="flex size-6 shrink-0 items-center justify-center rounded-3xl bg-brand-25 text-xs font-normal text-brand-700"
              title={`${index + 1}`}
            >
              {index + 1}
            </span>
            <span className="text-sm font-medium text-gray-700">
              <AppFormattedMessage
                id={templateSignatoryTypeTranslation[signatoryType.stakeholderType]}
              />
            </span>
          </div>
          {isGrantee ? (
            <span className="flex h-[26px] shrink-0 items-center rounded-3xl bg-forest-50 px-3 text-xs font-medium text-forest-600">
              <AppFormattedMessage id="AUTO_ASSIGNED" />
            </span>
          ) : (
            <StakeholderCombobox
              addButtonLabel={
                <>
                  + <AppFormattedMessage id="ADD_NEW" />
                </>
              }
              companyId={companyId}
              control={control}
              emptyListMessage={
                <AppFormattedMessage id="NO_TYPE_FOUND" values={{ type: typeLabel }} />
              }
              excludeStakeholderIds={excludeStakeholderIds}
              name={`${GRANT_BASE_PATH}.signatoryAssignments.${signatoryType.id}`}
              placeholder={<AppFormattedMessage id="CHOOSE" />}
              type={signatoryType.stakeholderType as StakeholderType}
            />
          )}
        </div>
        {isGrantee && (
          <ul className="-mt-2 flex flex-col gap-[2px] pl-8 text-sm text-gray-400">
            {grantItems.map((item, idx) => (
              <li className="text-[10px] leading-4" key={item.stakeholder?.id ?? `grantee-${idx}`}>
                {item.stakeholder?.fullName ?? '—'}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  },
);
ConfirmRecipientCard.displayName = 'ConfirmRecipientCard';

const getAssignedStakeholderId = (assigned: unknown): null | string => {
  if (!assigned || typeof assigned !== 'object' || !('id' in assigned)) return null;
  const id = assigned.id;
  return typeof id === 'string' && id.trim() !== '' ? id : null;
};

export const BulkGrantStepFourth: FC<ChildrenFormProps> = ({
  companyId,
  control,
  formData,
  handleCloseModal,
  nextFormStep,
  prevFormStep,
  setFormData,
}) => {
  const grantItemsWatch = useWatch({
    control,
    name: `${GRANT_BASE_PATH}.grantItems`,
    defaultValue: [],
  });
  const grantItems = useMemo(() => grantItemsWatch ?? [], [grantItemsWatch]);
  const signatoryAssignments = useWatch({
    control,
    name: `${GRANT_BASE_PATH}.signatoryAssignments`,
    defaultValue: {},
  });
  const selectedTemplateId = useWatch({
    control,
    name: `${GRANT_BASE_PATH}.selectedTemplateId`,
    defaultValue: '',
  });

  const { template } = useTemplateById(selectedTemplateId?.trim() ?? null);

  const processedData = useMemo(() => {
    const ids = grantItems.map((g) => g.stakeholder?.id).filter(Boolean);
    const granteeSet = new Set(ids);
    const sortedTypes = [...(template?.signatoryTypes ?? [])].sort(sortByOrder);
    const nonGranteeTypes = sortedTypes.filter((st) => st.stakeholderType !== GRANTEE);
    const canProceedStatus =
      nonGranteeTypes.length === 0 ||
      nonGranteeTypes.every(
        (st) => getAssignedStakeholderId(signatoryAssignments?.[st.id]) !== null,
      );
    return { canProceed: canProceedStatus, granteeIds: ids, granteeSet, sortedTypes };
  }, [grantItems, signatoryAssignments, template?.signatoryTypes]);

  useEffect(() => {
    const current = formData(`${GRANT_BASE_PATH}.signatoryAssignments`);
    if (template?.signatoryTypes?.length && current === undefined) {
      setFormData(`${GRANT_BASE_PATH}.signatoryAssignments`, {});
    }
  }, [template?.signatoryTypes?.length, formData, setFormData]);

  useEffect(() => {
    if (processedData.granteeIds.length === 0) return;
    const current = formData(`${GRANT_BASE_PATH}.signatoryAssignments`) ?? {};
    Object.entries(current).forEach(([signatoryId, assigned]) => {
      const id = getAssignedStakeholderId(assigned);
      if (id && processedData.granteeSet.has(id)) {
        setFormData(`${GRANT_BASE_PATH}.signatoryAssignments.${signatoryId}`, null);
      }
    });
  }, [processedData.granteeIds, processedData.granteeSet, formData, setFormData]);

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-6 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <h2 className="text-xs text-gray-500">
          <AppFormattedMessage id="CONFIRM_RECIPIENTS" />
        </h2>
        <div className="flex flex-col gap-4">
          {processedData.sortedTypes.map((st, index) => (
            <ConfirmRecipientCard
              companyId={companyId}
              control={control}
              excludeStakeholderIds={processedData.granteeIds}
              grantItems={grantItems}
              index={index}
              key={st.id}
              signatoryType={st}
            />
          ))}
        </div>
      </div>
      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
            onClick={prevFormStep}
            styleType="NONE"
            type="button"
          >
            <AppFormattedMessage id="BACK" />
          </Button>
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={!processedData.canProceed}
            onClick={nextFormStep}
            type="button"
          >
            <AppFormattedMessage id="NEXT" />
          </Button>
        </div>
      </div>
    </>
  );
};
